import React, { useMemo } from "react";
import { useQuery } from "urql";
import { sortBy } from "lodash";

import { Grid, Column } from "../../../../components/src";
import { BOND_ISINS } from "./constants";
import { FoNavInstrumentName } from "./FoNavInstrumentName";
import { GET_FONAV_BONDS } from "./queries";
import {
    FoNavBondGridRow,
    getTimeToMaturityYears,
    rateToPercent,
    toBpChangeGridValue,
    toBpGridValue,
    toYieldPercentChangeBpGridValue,
    toYieldPercentGridValue
} from "./utils";

interface FoNavBondsProps {
    startDate: string;
    endDate: string;
}

export function FoNavBonds({ startDate, endDate }: FoNavBondsProps): React.ReactElement {
    const [{ fetching, error, data }] = useQuery({
        query: GET_FONAV_BONDS,
        variables: {
            startDate,
            endDate,
            aliasIn: BOND_ISINS
        },
        pause: !startDate || !endDate,
        requestPolicy: "network-only"
    });

    const tableRows = useMemo((): FoNavBondGridRow[] => {
        if (!data?.instruments) {
            return [];
        }

        const rows: FoNavBondGridRow[] = data.instruments.map((instrument) => {
            const yieldStartPercent = rateToPercent(instrument.yieldStart);
            const yieldEndPercent = rateToPercent(instrument.yieldEnd);

            return {
                name: instrument.name,
                instrumentId: instrument._id,
                timeToMaturityYears: getTimeToMaturityYears(instrument.maturityDate, endDate),
                startValue: toBpGridValue(instrument.spreadStart),
                endValue: toBpGridValue(instrument.spreadEnd),
                change: toBpChangeGridValue(instrument.spreadStart, instrument.spreadEnd),
                yieldStartValue: toYieldPercentGridValue(yieldStartPercent),
                yieldEndValue: toYieldPercentGridValue(yieldEndPercent),
                yieldChange: toYieldPercentChangeBpGridValue(yieldStartPercent, yieldEndPercent),
                lastPriceDate: instrument.lastPrice?.date ?? ""
            };
        });

        return sortBy(rows, (row) => row.timeToMaturityYears ?? Number.MAX_SAFE_INTEGER);
    }, [data, endDate]);

    if (fetching) {
        return <p>Loading bonds...</p>;
    }

    if (error) {
        return <p>error: {JSON.stringify(error, null, 2)}</p>;
    }

    return (
        <Grid header="Bonds" data={tableRows} className="table-timeseries" tableClassName="table-xs" sortable>
            <Column
                field="name"
                className="nowrap left"
                format={(value: string, item: FoNavBondGridRow) => (
                    <FoNavInstrumentName name={value} instrumentId={item?.instrumentId} />
                )}
            />
            <Column
                field="timeToMaturityYears"
                title="Time to maturity (y)"
                className="nowrap left"
                format={(value: number | null) => {
                    if (value === null || typeof value === "undefined") {
                        return "-";
                    }
                    return value.toFixed(2);
                }}
            />
            <Column field="startValue" title="Start value (bp)" className="nowrap left" />
            <Column field="endValue" title="End value (bp)" />
            <Column field="change" title="Change (bp)" />
            <Column field="yieldStartValue" title="Start yield (%)" className="nowrap left" />
            <Column field="yieldEndValue" title="End yield (%)" />
            <Column field="yieldChange" title="Yield chg (bp)" />
            <Column field="lastPriceDate" title="Last price date" className="nowrap" format={(value: string) => value || "-"} />
        </Grid>
    );
}
